import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import {
  ClickAction,
  ClickTargetType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import styled from '@emotion/styled';
import { ModalType } from 'constants/modal';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { ArrowRightSlimIcon } from 'icons/ArrowRightSlimIcon';
import { InfoFilledIcon } from 'icons/InfoFilledIcon';
import { useRouter } from 'next/navigation';
import { useGetChannelByContentIdQuery } from 'queries/chat/useGetChannelByContentIdQuery';
import { useCallback, useEffect, useState } from 'react';
import { DescriptionBlock } from 'ui-components/common/DescriptionBlock';
import { Link } from 'ui-components/common/Link';
import { ContractProposalModal } from 'ui-components/modals/ContractProposalModal';
import { Modal } from 'ui-components/modals/Modal';
import { showToast } from 'utils/modal';
import { ModalHandler } from 'utils/ModalHandler';

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.ContentDetail,
};

interface ProposalBannerProps {
  content: ContentPublicRelayDto;
}

export const ProposalBanner = ({ content }: ProposalBannerProps) => {
  const router = useRouter();
  const [showBanner, setShowBanner] = useState(false);
  const { tracker } = useTrackingState();
  const [proposalCompleted, setProposalCompleted] = useState(false);
  const { partner } = useAuthState();
  const { data, isLoading } = useGetChannelByContentIdQuery(content.id, !!partner);

  const handleTrack = useCallback(
    (action: ClickAction, clickType: ClickTargetType) => {
      tracker.track(TrackingEventType.Click, { action, clickType, trackerExtraInfo });
    },
    [tracker],
  );

  const handleMoveChannel = useCallback(() => {
    if (data?.chatChannel) {
      router.push(`/chats/${data.chatChannel.id}`);
    }
  }, [data?.chatChannel, router]);

  const handleProposalModal = useCallback(() => {
    handleTrack(ClickAction.ToProposeContract, ClickTargetType.Banner);
    if (data?.chatChannel || proposalCompleted) {
      ModalHandler.show(ModalType.Confirm, {
        title: '작가에게 같은 회사에서 제안을 보낸 이력이 있어요.',
        confirmText: '메세지 보내기',
        cancelText: '취소',
        onConfirm: handleMoveChannel,
      });
      return;
    }

    if (!content?.profileId || !content?.title) {
      showToast('작품 정보를 불러오는 중이에요. 잠시 후 다시 시도해주세요.');
      return;
    }

    ModalHandler.show(ModalType.ContractProposal, {
      contentId: content.id,
      title: content.title,
      authorProfileId: content.profileId,
      onConfirm: () => {
        setProposalCompleted(true);
      },
    });
  }, [
    handleTrack,
    data?.chatChannel,
    proposalCompleted,
    content.profileId,
    content.title,
    content.id,
    handleMoveChannel,
  ]);

  useEffect(() => {
    if (!content.isProposalWanted || !partner?.verifiedAt) {
      return () => {};
    }

    if (!isLoading) {
      if (data?.chatChannel) {
        setProposalCompleted(true);
      }
      setShowBanner(true);
    }

    return () => {
      setShowBanner(false);
    };
  }, [content.id, content.isProposalWanted, data, isLoading, partner?.verifiedAt]);

  if (!showBanner) {
    return null;
  }

  if (data?.chatChannel || proposalCompleted) {
    return (
      <Container onClick={handleMoveChannel}>
        <DescriptionBlock
          icon={<InfoFilledIcon className="size-[16px]" color={Color.Orange500} />}
          rightIcon={
            <Link href={`/chats/${data?.chatChannel?.id}`}>
              <StyledIconWrapper>
                <ArrowRightSlimIcon size={12} color={Color.Orange500} />
              </StyledIconWrapper>
            </Link>
          }
          message="해당 작품에 제안을 보낸 이력이 있어요."
          isBorderRadius={false}
          bgColor={Color.Orange100}
          color={Color.Orange500}
        />
      </Container>
    );
  }

  return (
    <>
      <Container onClick={handleProposalModal}>
        <DescriptionBlock
          icon={<InfoFilledIcon className="size-[16px]" color={Color.Orange500} />}
          rightIcon={
            <StyledIconWrapper>
              <StyledText>작가에게 제안하기</StyledText>
              <ArrowRightSlimIcon size={12} color={Color.Orange500} />
            </StyledIconWrapper>
          }
          message="미계약 작품이에요."
          isBorderRadius={false}
          bgColor={Color.Orange100}
          color={Color.Orange500}
        />
      </Container>
      <Modal>
        <ContractProposalModal />
      </Modal>
    </>
  );
};

const StyledIconWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const StyledText = styled.div`
  ${Typography.XXXSmall};
  padding-right: 2px;
`;

const Container = styled.div``;
